import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { useInView } from "react-intersection-observer";
import ReactPlayer from "react-player";
import { Carousel } from "react-responsive-carousel";
import "react-responsive-carousel/lib/styles/carousel.min.css";

import useCollection, { usePaginatedCollections } from "../../hooks/useCollection";
import { cn } from "../../lib/utils";
import CartStorage from "../../services/frontend/CartStorage";
import Basket from "../Cart/Basket";
import StaticInfoBanner from "../Infobanner/StaticInfoBanner";
import NavBar from "../Navbar";
import Reviews from "../Reviews/Reviews";
import SideNavDrawer from "../SideNavDrawer";
import { Skeleton } from "../Skeleton";
import SkeletonCard from "../SkeletonCard";
import styles from "./featured-home.module.css";
import FeaturedCollectionList from "./FeaturedCollectionList";
import FeaturedCollectionWithProd from "./FeaturedCollectionWithProd";
import { fetchProductsForCollections } from "./utils";

function capitalizeFirstLetter(string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

const FeaturedHome = ({
  theme,
  store,
  updateHomeActive,
  heroMedia,
  heroMediaImages,
  heroMediaType,
  heroTitle,
}) => {
  const [cart, setCart] = useState(CartStorage.getInitialCart());
  const [isCartInitialized, setIsCartInitialized] = useState(false);
  const [showCart, setShowCart] = useState(false);
  const [showSideNav, setShowSideNav] = useState(false);
  const [collectionsWithProducts, setCollectionsWithProducts] = useState([]);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const router = useRouter();

  const { data: fetchedCollections, isLoading: isCollectionsLoading } = useCollection(store.id, "");

  const collections = fetchedCollections ? fetchedCollections.collections : [];
  const featuredCollections = collections
    .filter((col) => col.isFeatured && col.isActive)
    .sort((a, b) => a.sortOrder - b.sortOrder);

  const isVerticalScroll = theme?.collectionScrollDirection === "vertical";
  const hasFeaturedCollections = featuredCollections && featuredCollections.length > 0;

  // Setup intersection observer for the pagination trigger element
  const { ref, inView } = useInView({
    threshold: 0.1, // Trigger when at least 10% of the element is visible
    triggerOnce: false,
  });

  // Initialize cart
  useEffect(() => {
    const initialize = async () => {
      const initialCartData = await CartStorage.initializeCart();
      setCart(initialCartData);
      setIsCartInitialized(true);
    };
    initialize();
  }, []);

  const updateAndSaveCart = async (newCartData) => {
    setCart(newCartData);
    await CartStorage.saveCart(newCartData);
  };

  const updateAddedToCart = (productId, value) => {
    console.log(`FeaturedHome: Product ${productId} added status: ${value} (update logic needed)`);
  };

  const handleShowCart = (boolVal = false) => {
    setShowCart(boolVal);
  };

  // Fetch collections with pagination
  const {
    data: collectionsData,
    isLoading: isCollectionsInitialLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = usePaginatedCollections(store.id);

  // Process newly loaded collections to fetch their products
  useEffect(() => {
    if (!collectionsData) return;

    const processCollections = async () => {
      // Get all collections from all pages
      const newCollections = collectionsData.pages.flatMap((page) => page.collections || []);

      // Find only collections that haven't been processed yet by comparing IDs
      const processedCollectionIds = new Set(collectionsWithProducts.map((col) => col._id));
      const collectionsToProcess = newCollections.filter(
        (newCol) => !processedCollectionIds.has(newCol._id),
      );

      if (collectionsToProcess.length === 0) return;

      setIsLoadingProducts(true);
      try {
        const collectionsWithProductsData = await fetchProductsForCollections(
          store.id,
          collectionsToProcess,
          isVerticalScroll ? 4 : undefined,
        );

        setCollectionsWithProducts((prev) => [
          ...prev,
          ...collectionsWithProductsData.filter(
            (newCol) => !processedCollectionIds.has(newCol._id),
          ),
        ]);
      } catch (error) {
        console.error("Error fetching products for collections:", error);
      } finally {
        setIsLoadingProducts(false);
      }
    };

    processCollections();
  }, [collectionsData, store.id, collectionsWithProducts]);

  // Load more collections when observer element comes into view
  // Only trigger when inView and we have more pages and we're not already loading
  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage && !isLoadingProducts) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, fetchNextPage, isFetchingNextPage, isLoadingProducts]);

  // Combined loading state
  // const isLoading = isCollectionsInitialLoading || isFetchingNextPage || isLoadingProducts;

  // Render loading skeletons
  const renderLoadingSkeletons = (key) => (
    <div className="m-5" key={key}>
      <Skeleton className="mx-auto mb-[30px] mt-[50px] h-8 w-28 text-center font-bold text-primary" />
      <div className="hide-scrollbar flex flex-row gap-4 overflow-x-auto overflow-y-hidden whitespace-nowrap">
        {Array(2)
          .fill(0)
          .map((_, i) => (
            <SkeletonCard
              className="m-[4px_5px_5px_4px] w-[10px] rounded-[50px] px-5 py-[4px]"
              key={i}
              imageClass="w-[calc((var(--max-width)/1.5)-50px)] h-[calc((var(--max-width)/1.5)-50px)]"
            />
          ))}
      </div>
    </div>
  );

  return (
    <>
      <StaticInfoBanner text={store?.storeBanner?.text} />

      <div
        className={`sticky top-0 w-full ${heroMediaType === "image" ? "h-full" : "relative"} z-50 flex justify-center`}
      >
        {isCartInitialized && cart?.products?.length > 0 && (
          <Basket
            isBasketOpen={showCart}
            setCart={updateAndSaveCart}
            cartData={cart}
            updateAddedToCart={updateAddedToCart}
            handleShowCart={handleShowCart}
            store={store}
            setRefresh={() => {}}
          />
        )}

        <NavBar
          cartActive={isCartInitialized && cart.products.length > 0}
          hideInAdvance={false}
          homeActive={true}
          storeName={heroTitle}
          handleShowCart={handleShowCart}
          store={store}
          theme={theme}
          toggleSideNav={() => setShowSideNav(!showSideNav)}
          homePage
        />

        <SideNavDrawer
          isOpen={showSideNav}
          toggleDrawer={() => setShowSideNav(!showSideNav)}
          store={store}
        />
      </div>

      {heroMediaType === "image" ? (
        <Carousel
          autoPlay
          infiniteLoop
          showThumbs={false}
          showArrows={false}
          showStatus={false}
          className={cn(styles.carouselImageContainer)}
          onClickItem={() => router.push("/shop")}
          preventMovementUntilSwipeScrollTolerance
          swipeScrollTolerance={50}
        >
          {heroMediaImages.map((imageUrl, index) => {
            const imageName = imageUrl.substring(imageUrl.lastIndexOf("/") + 1);
            const imgurlNew = store.cohort?.includes(1)
              ? `https://ik.imagekit.io/dm2asset/dm2assetfull/${imageName}`
              : `https://dm2buy-resize-dynamic-cebdcaefgydgh6hu.z02.azurefd.net/dm2buy/${imageName}?width=500&quality=80`;
            return (
              <div key={imageName}>
                <img
                  src={imgurlNew}
                  alt={imageName}
                  className="h-[656px] w-[520px] object-cover pb-8"
                  fetchpriority={index === 0 ? "high" : "auto"}
                />
              </div>
            );
          })}
        </Carousel>
      ) : (
        <div className="relative h-screen max-h-[90vh] w-full" onClick={() => router.push("/shop")}>
          <ReactPlayer
            url={heroMedia}
            playing
            loop
            muted
            playsinline
            width="100%"
            height="100%"
            className={styles.carouselVideoContainer}
          />
        </div>
      )}

      <div className="z-10 mt-2.5 flex w-full flex-col items-center">
        <button
          onClick={() => updateHomeActive(false)}
          className="flex h-10 w-36 items-center justify-center rounded-md bg-primary text-sm text-fontPrimary"
        >
          SHOP NOW
        </button>
      </div>

      {hasFeaturedCollections && (
        <FeaturedCollectionList
          featuredCollections={featuredCollections}
          isLoading={isCollectionsLoading}
        />
      )}

      <div className="min-h-[400px] w-full">
        {isCollectionsInitialLoading ? (
          <div>
            {Array(2)
              .fill(0)
              .map((_, index) => renderLoadingSkeletons(`initial-skeleton-${index}`))}
          </div>
        ) : collectionsWithProducts.length > 0 ? (
          <>
            {collectionsWithProducts.map((collection) => (
              <FeaturedCollectionWithProd
                cohort={store.cohort}
                key={collection._id}
                collectionName={capitalizeFirstLetter(collection.name ? collection.name : "")}
                themeData={theme}
                productsData={collection.products || []}
              />
            ))}

            {/* Only show loading indicator when actually fetching next page */}
            {(isFetchingNextPage || isLoadingProducts) &&
              renderLoadingSkeletons("loading-more-skeleton")}

            {/* Place the intersection observer ref in a separate element after all collections */}
            {hasNextPage && <div ref={ref} className="h-12 w-full" aria-hidden="true" />}
          </>
        ) : (
          <div className="flex h-full items-center justify-center">
            <p className="text-gray-500"></p>
          </div>
        )}
      </div>

      <Reviews storeId={store.id} />
    </>
  );
};

export default FeaturedHome;
